import Typography from '@mui/joy/Typography';
import { Box, Button } from '@mui/material';
import { DelayButton } from 'module/documents/components/components';
import React, { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export type SignatureBoxRef = {
	submitSignature: () => Promise<string | null>;
};

// { boxTitle?: string; existingPath?: string; onSubmit: (file: File) => void }
type SignatureBoxProps = {
	boxTitle?: string;
	existingPath?: string;
	onSubmit: (file: File) => Promise<string | null> | Promise<void>; 
	onSignComplete?: () => void; 
	onClearComplete?: () => void;
};

const SignatureBox = forwardRef<SignatureBoxRef, SignatureBoxProps>(
	({ onSubmit, existingPath, boxTitle, onSignComplete, onClearComplete }, ref) => {

	const sigCanvasRef = useRef<SignatureCanvas>(null);
	const isBlobUrl = existingPath?.startsWith('blob:');
	const handleClear = () => {
		sigCanvasRef.current?.clear();
		if (onClearComplete) {
			onClearComplete();
		}
	};

	const handleSubmit = async () => {
		if (sigCanvasRef.current) {
			// Get the base64 data URL of the signature
			const trimmedDataURL = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');

			// Convert the base64 data URL to a File object
			const file = dataURLToFile(trimmedDataURL, 'signature.png');

			// Trigger the submit callback with the file
			onSubmit(file);
		}
	};

	useImperativeHandle(ref, () => ({
		async submitSignature() {
			if (sigCanvasRef.current) {
				
				// Get the base64 data URL of the signature
				const trimmedDataURL = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');
	
				// Convert the base64 data URL to a File object
				const file = dataURLToFile(trimmedDataURL, 'signature.png');

				// Trigger the submit callback with the file
				const result = await onSubmit(file);  // ✅ Wait for handleSignatureSubmit() to return ID

				// Force it to return string or null, never undefined or void
				if (typeof result === 'string') {
					return result;
				}
				return null; // if void/undefined
			}
			return null;
		},
	}));

	// Function to convert base64 data URL to File
	const dataURLToFile = (dataUrl: string, fileName: string): File => {
		const arr = dataUrl.split(',');
		const mime = arr[0].match(/:(.*?);/)[1];
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], fileName, { type: mime });
	};

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="flex-start"
			justifyContent="center"
			pb="2rem"
		>
			<Typography level="title-sm">
				{boxTitle && <span style={{ color: 'red' }}>{boxTitle}</span>}
				{!boxTitle && (existingPath || existingPath === '' ? 'Employee signature' : 'Sign Below')}
				</Typography>
			<Box alignSelf="flex-start">
				{existingPath && existingPath !== '' ? (
					<Box width="200px" height="100px" margin="16px">
						<img
							src={isBlobUrl ? existingPath : process.env.REACT_APP_URL + existingPath}
							style={{
								width: 200,
								height: 100,
								// filter: 'blur(18px)',
							}}
						/>
					</Box>
				) : (
					<SignatureCanvas
						ref={sigCanvasRef}
						penColor="black"
						onEnd={() => {
							if (onSignComplete) {
								onSignComplete();
							}
						}}
						canvasProps={{
							width: 400,
							height: 200,
							className: 'sigCanvas',
							style: { border: '1px solid black' },
						}}
					/>
				)}
			</Box>
			{/* <Box mt={2} visibility={existingPath ? 'hidden' : 'visible'}> */}
			<Box mt={2} >
				<Button
					variant="outlined"
					color="secondary"
					onClick={handleClear}
				>
					Clear
				</Button>
			</Box>
		</Box>
	);
	}
);

SignatureBox.displayName = 'SignatureBox';

export default SignatureBox;
